import { computeHalfTrimWidth } from '../helpers/page-trim-width'
import { PrintBleedTintOverlay } from '../print-bleed'

type DoubleSpreadBleedOverlayAboveShapesProps = {
  bleedWidth: number
  bleedHeight: number
  bleedMargin: number
  trimWidth: number
  trimHeight: number
  centerGap: number
  /**
   * The in-trim safety band/labels always render regardless of this flag; only the outer
   * bleed-edge band/labels (and the pasteboard-corner fillers that complete its shape) are gated
   * on it — fed from the editor's "Show Bleed Bar" toggle. This component itself is therefore no
   * longer conditionally mounted on that toggle; it always renders so the safety band stays up.
   */
  showOutsideTrim: boolean
}

/**
 * Outer + inner print bleed and labels, above `ShapeManager` (z-6). `PrintBleedTintOverlay`’s
 * `PrintBleedTints` already draws the full in-trim and out-of-trim bands; we must not add a
 * second `InnerTrimBleedFrame` for non-spine edges here — a duplicate semi-transparent fill stacks
 * and makes “inner” yellow darker than the outer band. `centerGap` is passed for bindery-gutter
 * tint in `PrintBleedTints` (outer trim token, above the layout grid). The gutter column's own
 * fill and its adjoining in-trim segments are not drawn here — see
 * `DoubleSpreadGutterTintAboveShapes` (`center-canvas/components/screen-space-layers.tsx`) for why.
 */
export function DoubleSpreadBleedOverlayAboveShapes({
  bleedWidth,
  bleedHeight,
  bleedMargin,
  trimWidth,
  trimHeight,
  centerGap,
  showOutsideTrim,
}: DoubleSpreadBleedOverlayAboveShapesProps) {
  const pageTrimWidth = computeHalfTrimWidth(trimWidth)

  if (pageTrimWidth <= 0) {
    return null
  }

  return (
    <div className="pointer-events-none absolute inset-0" style={{ zIndex: 6 }}>
      <PrintBleedTintOverlay
        bleedWidth={bleedWidth}
        bleedHeight={bleedHeight}
        bleedMargin={bleedMargin}
        trimWidth={trimWidth}
        trimHeight={trimHeight}
        centerGap={centerGap}
        visible
        isSinglePage={false}
        zIndex={1}
        showOutsideTrim={showOutsideTrim}
      />
    </div>
  )
}
